import { Component, KeyboardEvent, ReactNode, SyntheticEvent } from 'react';
import Immutable from 'immutable';
import { APIPropTypes, PropTypes } from 'typed/imports';
import {
    ComponentEventHandler1, ComponentEventHandler2, ComponentEventHandler3, ConnectedProps, PureComponent, ReactEventHandler, connect
} from 'typed/components/utils';
import { draftToMarkdown, markdownToDraft } from 'markdown-draft-js';
import {
    ContentState, DraftHandleValue, EditorChangeType, EditorState, Modifier, RichUtils, SelectionState,
    convertFromRaw, convertToRaw, getDefaultKeyBinding
} from 'draft-js';
import { InjectedIntlProps, injectIntl, intlShape } from 'react-intl';
import { Editor } from 'react-draft-wysiwyg';
import Popup from 'components/common/Popup/Popup';
import FormattedResource, { Resource } from '../FormattedResource/FormattedResource';
import Listbox from '../Listbox/Listbox';
import common from '!!less-vars-loader!static/common.less';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import './WysiwygEditor.less';

// Component demo https://jpuri.github.io/react-draft-wysiwyg/#/demo

export interface MentionSuggestion {
    ID: number;
    FilterDisplayName: string;
    EditorDisplayName: string;
    EntityType: string; // UserLocal || CompanyFunction || CompanyVirtualTeam
}

interface MentionPopupData {
    top: number | null;
    bottom: number | null;
    left: number;
    popupContentMaxHeight: number | null;
    filter: string;
    filterStartPositionX: number;
    filterStartPositionY: number;
    filterStartCursorLeftOffset: number;
    filterStartCursorRightOffset: number;
    activeSuggestionIndex: number;
}

interface TypedWordData {
    text: string;
    startPositionInBlock: number;
    endPositionInBlock: number;
    cursorPositionInBlock: number;
}

interface BlockData {
    text: string;
    cursorPosition: number;
}

const
    { OrderedSet } = Immutable,

    IMAGE_PATTERN = /image.*/,

    MENTION_TRIGGER = '@',
    MENTION_WORD_SEPARATORS = '.,;:!?*#+()@[]', // regex testing does not work in FF

    // https://stackoverflow.com/questions/55352984/q-react-draft-wysiwyg-how-to-add-custom-label-to-blocktypes
    // https://github.com/jpuri/react-draft-wysiwyg/blob/master/src/config/defaultToolbar.js
    EditorLabelTranslationKeys: { [key: string]: string; } = {
        // BlockType
        'components.controls.blocktype.h1': 'wysiwyg.blocktype.h1',
        'components.controls.blocktype.h2': 'wysiwyg.blocktype.h2',
        'components.controls.blocktype.h3': 'wysiwyg.blocktype.h3',
        'components.controls.blocktype.blocktype': 'wysiwyg.blocktype',
        'components.controls.blocktype.normal': 'wysiwyg.blocktype.normal',

        // Inline
        'components.controls.inline.bold': 'wysiwyg.bold',
        'components.controls.inline.italic': 'wysiwyg.italic',
        'components.controls.inline.strikethrough': 'wysiwyg.strikethrough',

        // List
        'components.controls.list.unordered': 'wysiwyg.list.unordered',
        'components.controls.list.ordered': 'wysiwyg.list.ordered'
    },

    // https://draftjs.org/docs/advanced-topics-inline-styles
    StyleMap: { [key: string]: any; } = {
        'BOLD': {
            fontWeight: common.fontWeightSemiBold
        }
    },

    DraftHandleValueKey: { [key: string]: DraftHandleValue; } = {
        HANDLED: 'handled',
        NOT_HANDLED: 'not-handled'
    },

    EditorChangeTypeKey: { [key: string]: EditorChangeType; } = {
        CHANGE_BLOCK_DATA : 'change-block-data',
        SPLIT_BLOCK: 'split-block',
        INSERT_CHARACTERS: 'insert-characters'
    };

interface StoreProps { }

interface OwnProps extends InjectedIntlProps {
    value: string;
    autofocus?: boolean;
    placeholder?: Resource;
    hideToolbar?: boolean;
    inline?: boolean;
    mentionSuggestions?: MentionSuggestion[];
    onChange?: ComponentEventHandler1<string>;
    onBlur?: ComponentEventHandler2<string, SyntheticEvent<HTMLElement>>;
    onPasteImage?: ComponentEventHandler3<string, string, string>;
    onPasteText?: (sender: Component, text: string) => boolean;
    onMentioned?: ComponentEventHandler1<MentionSuggestion>;
    onSubmit?: ComponentEventHandler1<string>;
}

interface Props extends StoreProps, OwnProps, ConnectedProps { }

interface State {
    editorState: EditorState;
    initialized: boolean;
    suggestionQueryString: string;
    mentionPopupData: MentionPopupData | null;
}

class WysiwygEditor extends PureComponent<Props, State> {
    static readonly propTypes = {
        value: PropTypes.string,
        autofocus: PropTypes.bool,
        placeholder: FormattedResource.resourcePropType,
        hideToolbar: PropTypes.bool,
        inline: PropTypes.bool,
        mentionSuggestions: PropTypes.arrayOf(PropTypes.object),
        onChange: PropTypes.func,
        onBlur: PropTypes.func,
        onPasteImage: PropTypes.func,
        onPasteText: PropTypes.func,
        onMentioned: PropTypes.func,
        onSubmit: PropTypes.func,

        session: APIPropTypes.SessionInformation.isRequired,
        intl: intlShape.isRequired
    };

    static readonly defaultProps = {
        autofocus: false,
        placeholder: '',
        hideToolbar: false,
        inline: false
    };

    state: State = {
        editorState: EditorState.createEmpty(),
        initialized: false,
        suggestionQueryString: '',
        mentionPopupData: null
    };

    _editorReferece: any = null;
    _editorScrollTop: number | null = null;
    _mentioningTriggered = false;

    componentWillMount() {
        this.componentWillReceiveProps(this.props);
    }

    componentWillReceiveProps(nextProps: Props) {
        if (!this.state.initialized) {
            const
                nextValue = nextProps.value,
                markdownString = this._convertToMarkdownString(this.state.editorState);

            if (nextValue !== markdownString) {
                const
                    rawData = markdownToDraft(nextValue),
                    contentState = convertFromRaw(rawData),
                    editorState = EditorState.createWithContent(contentState);

                this.setState({
                    editorState,
                    initialized: true
                });
            }
            else {
                this.setState({
                    initialized: true
                });
            }
        }
    }

    componentWillUnmount() {
        this._editorReferece = null;
        this._editorScrollTop = null;
        this._mentioningTriggered = false;
    }

    _convertToMarkdownString(editorState: EditorState): string {
        const content = editorState.getCurrentContent(),
            rawObject = convertToRaw(content);

        return draftToMarkdown(rawObject);
    }

    _getTranslations(): { [key: string]: string; } {
        const
            { intl } = this.props,
            result: { [key: string]: string; } = {};

        Object.keys(EditorLabelTranslationKeys)
            .map(key => key as any as string)
            .forEach((key) => {
                result[key] = intl.formatMessage({ id: EditorLabelTranslationKeys[key]! });
            });

        return result;
    }

    _getCurrentLocale(): string {
        const { session } = this.props;
        return session.Language.split('-')[0];
    }

    _isMentionSupported(): boolean {
        const { mentionSuggestions } = this.props;
        return !!mentionSuggestions && mentionSuggestions.length > 0;
    }

    _isMentioning(): boolean {
        return this._isMentionSupported() && !!this.state.mentionPopupData;
    }

    _getFilteredMentionSuggestions(): MentionSuggestion[] {
        const
            { mentionSuggestions } = this.props,
            { mentionPopupData } = this.state,

            suggestionQueryString = mentionPopupData ?
                mentionPopupData.filter.toLowerCase() :
                null;

        if (!mentionSuggestions) {
            return [];
        }

        return mentionSuggestions.filter(({ FilterDisplayName }) => !suggestionQueryString || FilterDisplayName.toLowerCase().includes(suggestionQueryString));
    }

    _getFocucedBlockData(editorState: EditorState): BlockData {
        const
            content = editorState.getCurrentContent(),
            selection: SelectionState = editorState.getSelection(),
            cursorPosition = selection.getEndOffset(),
            block = content.getBlockForKey(selection.getAnchorKey()),
            text = block.getText();

        return {
            text,
            cursorPosition
        } as any as BlockData;
    }

    _getMentionTypedWord(editorState: EditorState): TypedWordData {
        const
            blockData = this._getFocucedBlockData(editorState),
            { cursorPosition, text } = blockData,
            mentionPopupData = this.state.mentionPopupData!,
            { filterStartCursorLeftOffset, filterStartCursorRightOffset } = mentionPopupData,

            textTail = text.slice(filterStartCursorLeftOffset),
            typedWord = textTail.slice(0, textTail.length - filterStartCursorRightOffset);

        return {
            text: typedWord.trim(),
            startPositionInBlock: filterStartCursorLeftOffset,
            endPositionInBlock: text.length - filterStartCursorRightOffset,
            cursorPositionInBlock: cursorPosition
        } as any as TypedWordData;
    }

    _getCursorRect(): DOMRect | null {
        const isSupported = typeof window.getSelection !== 'undefined';

        if (!isSupported) {
            return null;
        }

        const selection = window.getSelection();

        if (!selection || selection.rangeCount === 0) {
            return null;
        }

        const range = selection.getRangeAt(0);
        let clientRect = range ? range.getBoundingClientRect() : null;

        if (clientRect && clientRect.top !== 0 && clientRect.left !== 0) {
            return clientRect;
        }

        // fix first line selection detection
        let node = range.startContainer as any;
        clientRect = null;

        while (node && !clientRect) {
            if (node.getAttribute && node.getAttribute('data-block') === 'true') {
                clientRect = node.getBoundingClientRect();
            }

            node = node.parentNode as any;
        }

        return clientRect;
    }

    _getMentionPopupData = (editorState: EditorState): MentionPopupData | null => {
        const
            isMentionSupported = this._isMentionSupported(),
            cursorRect = this._getCursorRect();

        if (!cursorRect || !isMentionSupported) {
            this.setState({ mentionPopupData: null });
            return null;
        }

        const
            windowScrollTop = window.scrollY || window.pageYOffset || document.documentElement.scrollTop,
            popupPadding = 5,
            docElement = document.scrollingElement || document.documentElement,
            visibleDocHeight = docElement.clientHeight,
            showOnTop = cursorRect.bottom > visibleDocHeight * 2 / 3;

        let
            popupTop = null,
            popupBottom = null,
            popupContentMaxHeight = null;

        if (showOnTop) {
            popupBottom = windowScrollTop + cursorRect.top - popupPadding;
            popupContentMaxHeight = cursorRect.top - cursorRect.height - popupPadding * 3;
        }
        else {
            popupTop = windowScrollTop + cursorRect.bottom + popupPadding;
            popupContentMaxHeight = visibleDocHeight - cursorRect.bottom - cursorRect.height - popupPadding * 3;
        }

        const blockData = this._getFocucedBlockData(editorState);

        return {
            top: popupTop,
            bottom: popupBottom,
            left: cursorRect.left,
            popupContentMaxHeight,
            filter: '',
            filterStartPositionX: cursorRect.x,
            filterStartPositionY: cursorRect.y,
            filterStartCursorLeftOffset: blockData.cursorPosition - 1,
            filterStartCursorRightOffset: blockData.text.length - blockData.cursorPosition,
            activeSuggestionIndex: 0
        } as any as MentionPopupData;
    };

    _setEditorReference = (ref: any) => {
        if (!ref || !!this._editorReferece) {
            return;
        }

        this._editorReferece = ref;

        if (this.props.autofocus) {
            this._setFocus(true);
        }
    };

    _replaceMentionSuggestion(editorState: EditorState, suggestion: MentionSuggestion) {
        if (!suggestion || !this._isMentioning()) {
            this.setState({ mentionPopupData: null });
            return;
        }

        if (this.props.onMentioned) {
            this.props.onMentioned(this, suggestion);
        }

        const
            contentState: ContentState = editorState.getCurrentContent(),
            selectionState: SelectionState = editorState.getSelection(),

            typedWordData = this._getMentionTypedWord(editorState),

            contentAfterReplace = Modifier.replaceText(
                contentState,
                selectionState.merge({
                    anchorOffset: typedWordData.startPositionInBlock,
                    focusOffset: typedWordData.endPositionInBlock
                }),
                suggestion.EditorDisplayName,
                OrderedSet.of('BOLD') // https://www.npmjs.com/package/immutable https://stackoverflow.com/questions/48002275/how-to-create-a-new-draftinlinestyle
            ),

            editorStateAfterReplace = EditorState.push(
                editorState,
                contentAfterReplace,
                EditorChangeTypeKey.CHANGE_BLOCK_DATA
            ),

            contentStateAfterReplaceAndInsert = Modifier.insertText(
                contentAfterReplace,
                contentAfterReplace.getSelectionAfter(),
                ' '
            ),

            editorStateAfterReplaceAndInsert = EditorState.push(
                editorStateAfterReplace,
                contentStateAfterReplaceAndInsert,
                EditorChangeTypeKey.CHANGE_BLOCK_DATA
            );

        this.setState({
            editorState: EditorState.forceSelection(editorStateAfterReplaceAndInsert, contentStateAfterReplaceAndInsert.getSelectionAfter()),
            mentionPopupData: null
        });
    }

    _scrollToCursor = (scrollToTheBottomOfActiveElement = false) => {
        if (!this._editorReferece) {
            return;
        }

        const
            { editorState } = this.state,
            selectionState: SelectionState = editorState.getSelection(),
            focusedBlockKey = selectionState.getStartKey(),

            rootElement: HTMLElement = this._editorReferece,
            scrollableContainer = rootElement!.parentElement!.parentElement as HTMLElement,

            blocksWrapper = rootElement.lastChild as HTMLElement,
            children = [...blocksWrapper.children] as HTMLElement[],
            blocks = children.map(element => ['ul', 'ol'].includes(element.nodeName.toLowerCase()) ? [...element.children] : element).flatten() as HTMLElement[],

            focusedBlockIndex: number = blocks.findIndex((element: any) => element.dataset.offsetKey.startsWith(focusedBlockKey));

        if (focusedBlockIndex < 0) {
            return;
        }

        const
            focusedBlock: HTMLElement = blocks[focusedBlockIndex],

            blockLineHeight = 20, // depends on block type, so has to be calculated
            blockHeight = focusedBlock.clientHeight,

            focusedBlockFirstLineBottom = focusedBlock.offsetTop + blockLineHeight,
            focusedBlockBottom = focusedBlock.offsetTop + blockHeight,
            heightToShow = scrollToTheBottomOfActiveElement ? focusedBlockBottom : focusedBlockFirstLineBottom,

            maxVisiblePosition = scrollableContainer.scrollTop + scrollableContainer.clientHeight,
            diff = heightToShow - maxVisiblePosition;

        if (diff > 0) {
            scrollableContainer.scrollTop += diff + parseInt(common.commonVerticalMargin);
            return;
        }

        const scrollShouldNotBeChanged = this._editorScrollTop !== null && this._editorScrollTop + scrollableContainer.clientHeight > heightToShow;
        if (scrollShouldNotBeChanged) {
            scrollableContainer.scrollTop = this._editorScrollTop!;
            return;
        }

        const moveScrollToTop = scrollableContainer.scrollTop > heightToShow;

        if (moveScrollToTop) {
            scrollableContainer.scrollTop += focusedBlock.offsetTop - scrollableContainer.scrollTop - parseInt(common.commonVerticalMargin);
        }
    };

    _setFocus = (moveFocusToEnd = false) => {
        if (this._editorReferece) {
            this._editorReferece.focus();
        }

        if (moveFocusToEnd) {
            this.setState({
                editorState: EditorState.moveFocusToEnd(this.state.editorState)
            });
        }
    };

    _onEditorStateChange = (editorState: EditorState) => {
        const
            currentContentState: ContentState = this.state.editorState.getCurrentContent(),
            newContentState: ContentState = editorState.getCurrentContent(),

            // https://draftjs.org/docs/api-reference-editor-change-type/#split-block
            lastChangeType: string = editorState.getLastChangeType(),
            enterClicked = currentContentState !== newContentState && lastChangeType === EditorChangeTypeKey.SPLIT_BLOCK,
            charactersInserted = currentContentState !== newContentState && lastChangeType === EditorChangeTypeKey.INSERT_CHARACTERS,

            rootElement: HTMLElement | null = this._editorReferece || null,
            scrollableContainer = rootElement ? rootElement!.parentElement!.parentElement as HTMLElement : null,

            isMentioning = this._isMentioning();

        let
            { mentionPopupData } = this.state,
            newEditorState: EditorState = editorState,
            forceUpdate = false;

        if (isMentioning && !this._mentioningTriggered) {
            const
                typedWordData = this._getMentionTypedWord(editorState),
                typedWord = typedWordData.text,
                cursorPosition = this._getCursorRect(),

                blockLineHeight = 20, // depends on block type, so has to be calculated
                yPositionDiff = cursorPosition ? Math.abs(mentionPopupData!.filterStartPositionY - cursorPosition.y) : 0, // fix for FF

                isCursorInMentioning = yPositionDiff < blockLineHeight / 2 &&
                    typedWordData.startPositionInBlock < typedWordData.cursorPositionInBlock &&
                    typedWordData.endPositionInBlock >= typedWordData.cursorPositionInBlock;

            if (typedWord.startsWith(MENTION_TRIGGER) && isCursorInMentioning) {
                const mentionQueryString = typedWord.length > 1 ?
                    typedWord.slice(1).toLowerCase() :
                    '';

                mentionPopupData = { ...mentionPopupData!, filter: mentionQueryString };
            }
            else {
                mentionPopupData = null;
            }

            forceUpdate = true;
        }

        if (charactersInserted && this._mentioningTriggered) {
            this._mentioningTriggered = false;
            mentionPopupData = this._getMentionPopupData(editorState);
            forceUpdate = true;

            // prevent loosing editor area focus
            newEditorState = EditorState.acceptSelection(
                editorState,
                editorState.getSelection().merge({
                    hasFocus: true
                })
            );
        }

        this._editorScrollTop = scrollableContainer ? scrollableContainer.scrollTop : null;

        this.setState({
            editorState: newEditorState,
            mentionPopupData
        }, () => {
            if (enterClicked) {
                // fix scroll issue
                this._scrollToCursor();
            }

            if (forceUpdate) {
                this.forceUpdate();
            }

            this._editorScrollTop = null;
        });

        if (this.props.onChange) {
            const markdownString = this._convertToMarkdownString(newEditorState);

            if (markdownString !== this.props.value) {
                this.props.onChange(this, markdownString);
            }
        }
    };

    _onBlur = (event: SyntheticEvent<HTMLElement>) => {
        if (this.props.onBlur) {
            const markdownString = this._convertToMarkdownString(this.state.editorState);
            this.props.onBlur(this, markdownString, event);
        }
    };

    _onImageFileParced = (imageFile: any, event: any) => {
        if (!this.props.onPasteImage) {
            return;
        }

        let fileExtension = '';
        const dotIndex = imageFile.name.lastIndexOf('.');

        if (dotIndex >= 0) {
            fileExtension = imageFile.name.substring(dotIndex);
        }

        const mimeType = imageFile.type,
            dataURL = event.target.result;

        this.props.onPasteImage(this, fileExtension, mimeType, dataURL);
        this._setFocus();
    };

    _onPasteFile = (files: Blob[]) => {
        if (!this.props.onPasteImage) {
            return;
        }

        const imageFile = files.find((file: Blob) => file.type.match(IMAGE_PATTERN));

        if (!imageFile) {
            return;
        }

        const reader = new FileReader();
        reader.onload = this._onImageFileParced.bind(this, imageFile);
        reader.readAsDataURL(imageFile);
    };

    _onPasteText = (text: string, _html: string, _editorState: EditorState): DraftHandleValue => {
        if (this.props.onPasteText) {
            const handled: boolean = this.props.onPasteText(this, text);
            return handled ? DraftHandleValueKey.HANDLED : DraftHandleValueKey.NOT_HANDLED;
        }

        // fix incorrect paste behavior for bulleted lists
        return DraftHandleValueKey.NOT_HANDLED;
    };

    _onBeforeInput = (chars: string, editorState: EditorState, _eventTimeStamp: number): DraftHandleValue => {
        if (!this._isMentionSupported()) {
            return DraftHandleValueKey.NOT_HANDLED;
        }

        switch (true) {
            case chars === MENTION_TRIGGER:
                const
                    blockData = this._getFocucedBlockData(editorState),
                    { text, cursorPosition } = blockData,
                    previousLetter = cursorPosition > 0 && text.length > 0 ? text[cursorPosition - 1] : '',
                    startMentioning = previousLetter.length === 0 || previousLetter === ' ' || MENTION_WORD_SEPARATORS.includes(previousLetter);

                if (startMentioning) {
                    this._mentioningTriggered = true;
                }
                break;

            case MENTION_WORD_SEPARATORS.includes(chars):
                this.setState({ mentionPopupData: null });
                break;

            default: break;
        }

        return DraftHandleValueKey.NOT_HANDLED;
    };

    _onKeyDown = (e: KeyboardEvent<HTMLInputElement>): string | null => {
        const isMentioning = this._isMentioning();

        if (!isMentioning && this.props.inline && e.keyCode === 13) { // enter
            return 'enter-key-down';
        }

        if (!isMentioning) {
            return getDefaultKeyBinding(e);
        }

        switch (e.keyCode) {
            case 9: // tab
                return 'tab-key-down';

            case 13: // enter
                return 'enter-key-down';

            case 27: // escape
                return 'escape-key-down';

            case 38: // up
                return 'up-key-down';

            case 40: // down
                return 'down-key-down';

            default: return getDefaultKeyBinding(e);
        }
    };

    _onKeyDownCommand = (command: string, editorState: EditorState): DraftHandleValue | undefined => {
        const
            newEditorState = RichUtils.handleKeyCommand(editorState, command),
            isMentioning = this._isMentioning();

        if (newEditorState) {
            this.setState({ editorState: newEditorState });
            return DraftHandleValueKey.HANDLED;
        }

        if (command === 'enter-key-down' && !isMentioning) {
            if (this.props.onSubmit) {
                const markdownString = this._convertToMarkdownString(editorState);
                this.props.onSubmit(this, markdownString);
            }

            return DraftHandleValueKey.HANDLED;
        }

        if (!isMentioning) {
            return DraftHandleValueKey.NOT_HANDLED;
        }

        const mentionPopupData = this.state.mentionPopupData!;

        switch (command) {
            case 'tab-key-down':
            case 'enter-key-down':
                const
                    selectedSuggestionIndex = mentionPopupData.activeSuggestionIndex,
                    mentionSuggestions = this._getFilteredMentionSuggestions(),
                    suggestion = selectedSuggestionIndex >= 0 ? mentionSuggestions[selectedSuggestionIndex] : mentionSuggestions[0];

                this._replaceMentionSuggestion(editorState, suggestion);
                return DraftHandleValueKey.HANDLED;

            case 'escape-key-down':
                this.setState({ mentionPopupData: null });
                return DraftHandleValueKey.HANDLED;

            case 'up-key-down':
                const nextIndex = mentionPopupData.activeSuggestionIndex - 1;

                this.setState({
                    mentionPopupData: {
                        ...mentionPopupData,
                        activeSuggestionIndex: nextIndex > 0 ? nextIndex : 0
                    }
                });

                return DraftHandleValueKey.HANDLED;

            case 'down-key-down':
                const
                    suggestions = this._getFilteredMentionSuggestions(),
                    newIndex = mentionPopupData.activeSuggestionIndex + 1;

                this.setState({
                    mentionPopupData: {
                        ...mentionPopupData,
                        activeSuggestionIndex: newIndex < suggestions.length ? newIndex : suggestions.length - 1
                    }
                });

                return DraftHandleValueKey.HANDLED;

            default: return DraftHandleValueKey.NOT_HANDLED;
        }
    };

    _onWrapperKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        const isMentioning = this._isMentioning();

        if (event.keyCode === 27 && isMentioning) { // escape
            event.stopPropagation();
        }
    };

    _onSuggestionsMouseEnter = () => {
        const { mentionPopupData } = this.state;

        if (mentionPopupData) {
            this.setState({
                mentionPopupData: {
                    ...mentionPopupData,
                    activeSuggestionIndex: -1
                }
            });
        }
    };

    _onSuggestionsMouseLeave = () => {
        const { mentionPopupData } = this.state;

        if (mentionPopupData) {
            this.setState({
                mentionPopupData: {
                    ...mentionPopupData,
                    activeSuggestionIndex: 0
                }
            });
        }
    };

    private _onSuggestionsMouseDown: ReactEventHandler = (event) => {
        // prevent loosing editor area focus
        event.preventDefault();
        event.stopPropagation();
    };

    private _onSuggestionChange: ComponentEventHandler1<string> = (_sender, selectOptionID) => {
        const
            { mentionSuggestions } = this.props,
            { editorState } = this.state,

            [entityType, entityID] = selectOptionID.split('-'),
            suggestion = mentionSuggestions!.find(({ ID, EntityType }) => ID === +entityID && EntityType === entityType)!;

        this._replaceMentionSuggestion(editorState, suggestion);
    };

    private _renderMentionSuggestionsPopup(): ReactNode {
        const { mentionPopupData } = this.state;

        if (!mentionPopupData) {
            return null;
        }

        const mentionSuggestions = this._getFilteredMentionSuggestions();

        return (
            <Popup
                top={mentionPopupData.top}
                bottom={mentionPopupData.bottom}
                left={mentionPopupData.left}
                fixed={false}
                className="mention-suggestions-modal"
                closeMode={Popup.CloseMode.OUTSIDE}
            >
                {
                    mentionSuggestions.length ?
                        <div
                            className="suggestions-wrapper"
                            style={{ maxHeight: `${mentionPopupData.popupContentMaxHeight}px` }}
                            onMouseEnter={this._onSuggestionsMouseEnter}
                            onMouseLeave={this._onSuggestionsMouseLeave}
                            onMouseDown={this._onSuggestionsMouseDown}
                        >
                            <Listbox onChange={this._onSuggestionChange}>
                                {
                                    mentionSuggestions
                                        .map((suggestion, index) =>
                                            <Listbox.Option
                                                key={`${suggestion.EntityType}-${suggestion.ID}`}
                                                value={`${suggestion.EntityType}-${suggestion.ID}`}
                                                className={index === mentionPopupData.activeSuggestionIndex ? 'active' : ''}
                                            >
                                                {suggestion.FilterDisplayName}
                                            </Listbox.Option>
                                        )
                                }
                            </Listbox>
                        </div> :
                        <div className="suggestions-wrapper">
                            <FormattedResource className="empty" resource={{ id: 'common.nothing_to_show' }} />
                        </div>
                }
            </Popup>
        );
    }

    render() {
        const
            { intl, placeholder, hideToolbar } = this.props,
            { editorState } = this.state,
            locale = this._getCurrentLocale(),
            translations = this._getTranslations(),

            placeholderText = typeof placeholder === 'object' && placeholder && typeof placeholder.id === 'string' ?
                intl.formatMessage(placeholder as any) :
                placeholder as any as string,

            isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

        return (
            <div ref="editor" className="wysiwyg-editor" onKeyDown={this._onWrapperKeyDown}>
                <Editor
                    editorState={editorState}
                    wrapperClassName={`editor-wrapper ${hideToolbar ? 'hidden-toolbar' : ''} ${!isSafari ? 'with-list-fix' : ''}`}
                    editorClassName="editor"
                    toolbarClassName="rich-text__toolbar"
                    placeholder={placeholderText !== undefined ? placeholderText : ''}
                    toolbar={{
                        options: ['inline', 'blockType', 'list'], // the toolbar and appears in the same order as specified
                        inline:
                        {
                            options: ['bold', 'italic', 'strikethrough']
                        },
                        blockType: {
                            options: [
                                'Normal',
                                'H1',
                                'H2',
                                'H3'
                            ]
                        },
                        list: {
                            options: ['unordered', 'ordered']
                        }
                    }}
                    editorRef={this._setEditorReference}
                    localization={{ locale, translations }}
                    customStyleMap={StyleMap}
                    // @ts-ignore
                    handlePastedFiles={this._onPasteFile}
                    // @ts-ignore
                    handlePastedText={this._onPasteText}
                    // @ts-ignore
                    handleBeforeInput={this._onBeforeInput}
                    // @ts-ignore
                    keyBindingFn={this._onKeyDown}
                    // @ts-ignore
                    handleKeyCommand={this._onKeyDownCommand}
                    onEditorStateChange={this._onEditorStateChange}
                    onBlur={this._onBlur}
                />
                {this._renderMentionSuggestionsPopup()}
            </div>
        );
    }
}

export default injectIntl(connect<StoreProps, OwnProps>(_state => ({}))(WysiwygEditor));
